import React, { useState, useEffect, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import UploadFile from '@mui/icons-material/UploadFile';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';

const CoinbaseModal = ({ 
  open, 
  onClose, 
  onSave, 
  isLoading, 
  error,
  editingSource // New prop for editing
}) => {
  const [formData, setFormData] = useState({
    display_name: '',
    source_name: 'coinbase',
    metadata: {
      name: '',
      private_key: ''
    }
  });

  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [jsonError, setJsonError] = useState('');
  const fileInputRef = useRef(null);

  // Pre-populate form when editing
  useEffect(() => {
    if (editingSource) {
      setFormData({
        display_name: editingSource.display_name || '',
        source_name: editingSource.source_name || 'coinbase',
        metadata: {
          name: editingSource.metadata?.name || '',
          private_key: editingSource.metadata?.private_key || ''
        }
      });
    } else {
      // Reset form for new source
      setFormData({
        display_name: '',
        source_name: 'coinbase',
        metadata: {
          name: '',
          private_key: ''
        }
      });
    }
    // Clear field errors when source changes
    setFieldErrors({});
    setJsonError('');
  }, [editingSource, open]);

  const handleInputChange = (field, value) => {
    if (field.startsWith('metadata.')) {
      const metadataField = field.replace('metadata.', '');
      setFormData(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          [metadataField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      setJsonError('Please select a valid JSON file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);
        
        // Validate JSON structure
        if (!jsonData.name || !jsonData.privateKey) {
          setJsonError('JSON must contain "name" and "privateKey" fields');
          return;
        }

        // Update form data with JSON values
        setFormData(prev => ({
          ...prev,
          metadata: {
            name: jsonData.name,
            private_key: jsonData.privateKey
          }
        }));

        setJsonError('');
        
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        setJsonError('Invalid JSON format');
      }
    };

    reader.onerror = () => {
      setJsonError('Error reading file');
    };

    reader.readAsText(file);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.display_name.trim()) {
      errors.display_name = 'Display name is required';
    }

    if (!formData.metadata.name.trim()) {
      errors['metadata.name'] = 'Name is required';
    }

    if (!formData.metadata.private_key.trim()) {
      errors['metadata.private_key'] = 'Private Key is required';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    onSave(formData);
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
      // Reset form data when closing
      setFormData({
        display_name: '',
        source_name: 'coinbase',
        metadata: {
          name: '',
          private_key: ''
        }
      });
      setFieldErrors({});
      setShowPrivateKey(false);
      setJsonError('');
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const isFormValid = formData.display_name.trim() && 
                     formData.metadata.name.trim() && 
                     formData.metadata.private_key.trim();

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="coinbase-dialog-title"
    >
      <DialogTitle id="coinbase-dialog-title">
        {editingSource ? 'Edit Coinbase Source' : 'Add Coinbase Source'}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Display Name"
            fullWidth
            value={formData.display_name}
            onChange={(e) => handleInputChange('display_name', e.target.value)}
            error={!!fieldErrors.display_name}
            helperText={fieldErrors.display_name || 'A friendly name for this connection'}
            disabled={isLoading}
            placeholder="e.g., My Coinbase Account"
          />

          {/* JSON Upload Section */}
          <Box sx={{ border: '1px dashed #ccc', borderRadius: 1, p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Or upload a JSON file with your credentials
            </Typography>
            <Button
              variant="outlined"
              startIcon={<UploadFile />}
              onClick={handleUploadClick}
              disabled={isLoading}
              sx={{ mb: 1 }}
            >
              Upload JSON File
            </Button>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
              Expected format: {"{ \"name\": \"your_name\", \"privateKey\": \"your_private_key\" }"}
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={() => window.open('/coinbase-credentials-example.json', '_blank')}
              disabled={isLoading}
              sx={{ textTransform: 'none' }}
            >
              Download example JSON file
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            {jsonError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {jsonError}
              </Alert>
            )}
          </Box>

          <TextField
            label="Name"
            fullWidth
            value={formData.metadata.name}
            onChange={(e) => handleInputChange('metadata.name', e.target.value)}
            error={!!fieldErrors['metadata.name']}
            helperText={fieldErrors['metadata.name'] || 'Your Coinbase API Name'}
            disabled={isLoading}
            placeholder="Enter your API Name"
          />

          <TextField
            label="Private Key"
            fullWidth
            type={showPrivateKey ? 'text' : 'password'}
            value={formData.metadata.private_key}
            onChange={(e) => handleInputChange('metadata.private_key', e.target.value)}
            error={!!fieldErrors['metadata.private_key']}
            helperText={fieldErrors['metadata.private_key'] || 'Your Coinbase Private Key'}
            disabled={isLoading}
            placeholder="Enter your Private Key"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPrivateKey(!showPrivateKey)}
                    edge="end"
                    disabled={isLoading}
                  >
                    {showPrivateKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={isLoading || !isFormValid}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isLoading ? (editingSource ? 'Updating...' : 'Adding...') : (editingSource ? 'Update Source' : 'Add Source')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CoinbaseModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  editingSource: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    display_name: PropTypes.string,
    source_name: PropTypes.string,
    metadata: PropTypes.shape({
      name: PropTypes.string,
      private_key: PropTypes.string
    })
  })
};

CoinbaseModal.defaultProps = {
  isLoading: false,
  error: null,
  editingSource: null
};

export default CoinbaseModal; 
